import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/16x16/opsgenie.svg';
import { ReactComponent as PagerIcon } from '@elementary/common-ui/assets/16x16/pager.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '@elementary/common-ui/assets/16x16/teams.svg';
import { ReactComponent as EmailIcon } from '@elementary/common-ui/assets/16x16/email.svg';
import {
  Accordion as AccordionBase,
  AccordionDetails as AccordionDetailsBase,
  AccordionSummary as AccordionSummaryBase,
  FilterChip,
  Flex,
  FlexCenter,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { EnvironmentPermission } from '@elementary/common-ui/types';
import { Tooltip, Typography } from '@mui/material';
import { AlertCategory, AlertFilterModel, IntegrationType } from 'shared/types';
import RuleMenu from './RuleMenu';

export type RuleAccordionProps = {
  id: string;
  name: string;
  channels: { name: string; type: IntegrationType }[];
  categories: Array<AlertCategory>;
  assetFilters?: Array<AlertFilterModel>;
  incidentFilters?: Array<AlertFilterModel>;
  active: boolean;
  onEdit: () => void;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onDelete: (id: string) => void;
  editPermission: EnvironmentPermission;
  index?: number;
  isDefault?: boolean;
  categoriesGatedByCloudTests?: boolean;
  cron?: string;
};

const categoriesMap: Record<AlertCategory, string> = {
  automated_volume_anomalies: 'Automated volume anomalies',
  automated_freshness_anomalies: 'Automated freshness anomalies',
  metric_anomalies: 'Metric anomalies',
  pipeline_performance: 'Pipeline task performance',
  custom_sql_tests: 'Custom SQL tests',
  dbt_generic_tests: 'dbt tests',
  dbt_source_freshness: 'dbt source freshness',
  model_build: 'Model errors',
  python_sdk: 'Python SDK tests',
  pipeline: 'Job failures',
};

const channelIconMap: Record<IntegrationType, JSX.Element> = {
  slack: <SlackIcon />,
  pager_duty: <PagerIcon />,
  microsoft_teams: <TeamsIcon />,
  microsoft_teams_bot: <TeamsIcon />,
  opsgenie: <OpsgenieIcon />,
  email: <EmailIcon />,
};

const Accordion = styled(AccordionBase)`
  &:hover {
    cursor: pointer;
  }
`;

const AccordionSummary = styled(AccordionSummaryBase)`
  overflow: hidden;

  &:hover {
    background-color: unset;
  }

  .MuiAccordionSummary-content {
    justify-content: space-between;
    align-items: center;
  }

  &.MuiAccordionSummary-root.Mui-expanded {
    border-bottom: none;
  }
`;

const AccordionDetails = styled(AccordionDetailsBase)<{ $hasIndex: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  gap: 16px;
  padding-top: 0;
  border-top: none;
  padding-left: ${({ $hasIndex }) => ($hasIndex ? '62px' : '24px')};
  background-color: unset;
`;

const IdBox = styled(FlexCenter)`
  position: absolute;
  left: 0;
  top: 0;
  background: ${({ theme }) => theme.palette.common.gray13};
  border-radius: 0 0 8px 0;
  padding: 4px;
  width: 24px;
  height: 24px;
`;

const Section = styled(Flex)`
  gap: 8px;
  max-width: 100%;
  overflow: hidden;
`;

const NameSection = styled(Section)`
  max-width: 881px;
  flex-direction: column;
`;

const DetailSection = styled(Section)`
  gap: 8px;
  align-items: center;
  align-self: stretch;
  flex-wrap: wrap;
`;

const SectionTitle = styled(Typography)`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.common.gray2};
  white-space: nowrap;
`;

const statusColorMap = {
  active: 'green100',
  inactive: 'gray11',
};

const StatusChip = styled(FlexCenter)<{ $active: boolean }>`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  border-radius: 100px;
  padding: 2px 6px;
  text-transform: uppercase;
  border: 1px solid
    ${({ theme, $active }) =>
      theme.palette.common[statusColorMap[$active ? 'active' : 'inactive']]};
  color: ${({ theme, $active }) =>
    theme.palette.common[statusColorMap[$active ? 'active' : 'inactive']]};
`;

const FiltersSection = ({
  title,
  filters,
}: {
  title: string;
  filters?: Array<AlertFilterModel>;
}) => (
  <DetailSection>
    <SectionTitle>{`${title}:`}</SectionTitle>
    {(filters || []).length > 0 ? (
      filters?.map((filter, i) => (
        <FlexCenter key={i} sx={{ gap: 1 }}>
          {i !== 0 && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'common.gray2',
              }}
            >
              AND
            </Typography>
          )}
          <FilterChip
            property={filter.property}
            relationship={filter.operator}
            value={filter.values}
          />
        </FlexCenter>
      ))
    ) : (
      <Typography sx={{ fontSize: '14px' }}>---</Typography>
    )}
  </DetailSection>
);

const formatChannelName = (channel: string) => {
  try {
    const parsed = JSON.parse(channel);
    if (Array.isArray(parsed.to)) return parsed.to.join(', ');
    if (typeof parsed.to === 'string') return parsed.to;
    return JSON.stringify(parsed);
  } catch {
    return `#${channel}`;
  }
};

const RuleAccordion = ({
  id,
  name,
  channels,
  categories,
  assetFilters,
  incidentFilters,
  active,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  editPermission,
  index,
  isDefault,
  categoriesGatedByCloudTests,
  cron,
}: RuleAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasPermission = useEnvPermission();
  const { data: account } = useAccount();

  const cloudTestsEnabled = useMemo(
    () => account?.cloud_tests_enabled,
    [account],
  );

  const showCategories =
    categories.length > 0 &&
    (!categoriesGatedByCloudTests || cloudTestsEnabled);

  const handleMenuItemClick = (val: string) => {
    switch (val) {
      case 'edit':
        return onEdit();
      case 'deactivate':
        return onDeactivate(id);
      case 'activate':
        return onActivate(id);
      case 'delete':
        return onDelete(id);
      default:
        return null;
    }
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <AccordionSummary>
        {index != null && (
          <Tooltip
            title={`If an alert matches multiple rules, it will be handled by\nthe first match according to the order of this list.`}
            componentsProps={{
              tooltip: { sx: { maxWidth: 'none', whiteSpace: 'pre-wrap' } },
            }}
          >
            <IdBox>
              <Typography sx={{ fontSize: '10px' }}>
                #
                <Typography
                  component="span"
                  sx={{ fontWeight: 'bold', fontSize: '12px' }}
                >
                  {index}
                </Typography>
              </Typography>
            </IdBox>
          </Tooltip>
        )}

        <NameSection>
          <Flex sx={{ alignItems: 'center', gap: 1 }}>
            <OverflowTooltip bold sx={{ width: 'unset !important' }}>
              {name}
            </OverflowTooltip>
            {isDefault && (
              <Typography sx={{ color: 'common.gray2', fontSize: '14px' }}>
                (Default)
              </Typography>
            )}
          </Flex>
          <Flex sx={{ flexWrap: 'wrap', rowGap: 1, columnGap: 3 }}>
            {channels.map((c, i) => (
              <Flex
                key={`${c.name}.${i}`}
                sx={{ gap: 1, alignItems: 'center', maxWidth: '100%' }}
              >
                <Flex>{channelIconMap[c.type]}</Flex>
                {c.name && (
                  <OverflowTooltip sx={{ fontSize: '14px' }}>
                    {formatChannelName(c.name)}
                  </OverflowTooltip>
                )}
              </Flex>
            ))}
          </Flex>
        </NameSection>

        <Flex sx={{ alignItems: 'center', gap: 1 }}>
          {cron && (
            <Tooltip title="Digest cadence" placement="top">
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'common.gray2',
                  whiteSpace: 'nowrap',
                }}
              >
                {cron}
              </Typography>
            </Tooltip>
          )}

          <StatusChip $active={!!active}>
            {active ? 'active' : 'inactive'}
          </StatusChip>

          {hasPermission(editPermission) && (
            <div onClick={(e) => e.stopPropagation()}>
              <RuleMenu
                onChange={handleMenuItemClick}
                isActive={active}
                isDefault={isDefault}
              />
            </div>
          )}
        </Flex>
      </AccordionSummary>

      <AccordionDetails $hasIndex={index != null}>
        {showCategories && (
          <DetailSection sx={{ minWidth: 'fit-content' }}>
            <SectionTitle>Test categories:</SectionTitle>
            <Flex
              sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
              divider={<Typography sx={{ fontSize: '14px' }}>|</Typography>}
            >
              {categories.map((category) => (
                <Typography key={category} sx={{ fontSize: '14px' }}>
                  {categoriesMap[category]}
                </Typography>
              ))}
            </Flex>
          </DetailSection>
        )}
        <FiltersSection title="Incident filters" filters={incidentFilters} />
        <FiltersSection
          title="Impacted assets filters"
          filters={assetFilters}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default RuleAccordion;
